<script lang="ts">
	import {
		Button,
		ButtonType,
		ButtonSize,
		Icon,
		Modal,
		DetailedSelect
	} from '@repo/commons/components'
	import type { SettingDefinition, SettingsContribution } from '@repo/commons/settings'
	import { initSettings, useSettings } from '@repo/commons/settings'
	import { createQuery, keepPreviousData } from '@tanstack/svelte-query'
	import { untrack } from 'svelte'
	import { t } from 'svelte-i18n'
	import ImportProgressView from './ImportProgressView.svelte'
	import SettingsContent from './SettingsContent.svelte'
	import { useSettingsSearch } from './useSettingsQueries.svelte.js'
	import { useSettingsDraft } from './useSettingsDraft.svelte.js'
	import { useSettingsImport } from './useSettingsImport.svelte.js'

	interface Props {
		visible?: boolean
		title?: string
		onClose: () => void
	}

	let { visible = true, title, onClose }: Props = $props()

	initSettings()
	const settings = useSettings()
	const search = useSettingsSearch()

	const contributionsQuery = createQuery({
		queryKey: ['settings', 'contributions'],
		queryFn: async () => {
			const res = await fetch('/api/shared/settings')
			if (!res.ok) throw new Error('Failed to fetch settings contributions')
			return (await res.json()) as SettingsContribution[]
		}
	})

	$effect(() => {
		const data = $contributionsQuery.data
		if (!data) return
		untrack(() => {
			for (const contribution of data) {
				settings.registerSettings(contribution)
			}
		})
	})

	const searchResultsQuery = $derived(
		createQuery({
			queryKey: ['settings', 'search', search.activeTab, search.debouncedSearchQuery],
			queryFn: async () => {
				const params = new URLSearchParams()
				if (search.debouncedSearchQuery) params.set('q', search.debouncedSearchQuery)
				if (search.activeTab) params.set('tab', search.activeTab)
				const res = await fetch(`/api/shared/settings/search?${params}`)
				if (!res.ok) throw new Error('Failed to search settings')
				const { results } = await res.json()
				return results as (SettingDefinition & { namespace: string; displayName: string })[]
			},
			enabled: $contributionsQuery.isSuccess,
			placeholderData: keepPreviousData
		})
	)

	const presetsQuery = createQuery({
		queryKey: ['settings', 'presets'],
		queryFn: async () => {
			const res = await fetch('/api/shared/settings/presets')
			if (!res.ok) throw new Error('Failed to fetch presets')
			return (await res.json()) as { id: string; name: string; description?: string }[]
		}
	})

	const isSearchPending = $derived(search.searchQuery !== search.debouncedSearchQuery)
	const isRefetching = $derived(
		isSearchPending || ($searchResultsQuery?.isFetching && $searchResultsQuery?.isPlaceholderData)
	)
	const isInitialLoading = $derived(
		$searchResultsQuery?.isLoading && !$searchResultsQuery?.isPlaceholderData
	)

	const draft = useSettingsDraft({
		settings,
		contributionsData: () => $contributionsQuery.data ?? [],
		searchResults: () => $searchResultsQuery?.data ?? [],
		presetsData: () => $presetsQuery.data ?? [],
		activeTab: () => search.activeTab
	})

	const importFlow = useSettingsImport(
		{
			settings,
			contributionsData: () => $contributionsQuery.data ?? []
		},
		(newDraft) => draft.replaceDraftValues(newDraft)
	)

	$effect(() => {
		draft.resetOnOpen(visible)
	})
</script>

<Modal
	{visible}
	title={title ?? $t('ViewSettings.title')}
	showCloseButton={false}
	closeOnBackdropClick={false}
	onclose={onClose}
>
	{#snippet headerActions()}
		{#if importFlow.state === 'idle'}
			<div class="header-actions">
				<Button
					class="action-link"
					buttonText={$t('ViewSettings.import')}
					iconProps={{ name: 'importFile', size: 'sm', class: 'mr-1' }}
					buttonType={ButtonType.LINK}
					onclick={importFlow.handleImportClick}
					buttonSize={ButtonSize.MEDIUM}
				/>
				<Button
					class="action-link"
					buttonText={$t('ViewSettings.export')}
					iconProps={{ name: 'download', size: 'sm', class: 'mr-1' }}
					buttonType={ButtonType.LINK}
					onclick={importFlow.handleExport}
					buttonSize={ButtonSize.MEDIUM}
				/>
				<div class="preset-select">
					<DetailedSelect
						items={draft.presetItems.map((item) => ({ ...item, title: $t(item.title) }))}
						selectedItem={draft.selectedPresetItem
							? { ...draft.selectedPresetItem, title: $t(draft.selectedPresetItem.title) }
							: null}
						searchable={false}
						clearable={false}
						placeholder={$t('ViewSettings.selectPreset')}
						position="right"
						onSelect={draft.handlePresetSelect}
					/>
				</div>
			</div>
		{/if}
	{/snippet}

	<input
		bind:this={() => importFlow.fileInput, (el) => (importFlow.fileInput = el)}
		type="file"
		accept=".json"
		class="hidden"
		onchange={importFlow.handleImportFile}
	/>

	{#if importFlow.state !== 'idle'}
		<ImportProgressView
			state={importFlow.state}
			message={importFlow.message}
			errorMessage={importFlow.errorMessage}
			progress={importFlow.progress}
			file={importFlow.file}
			onChooseAnother={importFlow.handleChooseAnotherFile}
		/>
	{:else}
		<SettingsContent
			tabs={draft.tabs}
			searchQuery={search.searchQuery}
			isRefetching={!!isRefetching}
			isInitialLoading={!!isInitialLoading}
			groupedByApp={draft.groupedByApp}
			getDraftValue={draft.getDraftValue}
			setDraftValue={draft.setDraftValue}
			isDraftValueLocked={draft.isDraftValueLocked}
			onTabChange={search.handleTabChange}
			onSearchInput={search.handleSearchInput}
			onSave={() => draft.handleSave(onClose)}
			onCancel={onClose}
		/>
	{/if}
</Modal>

<style lang="postcss">
	.header-actions {
		@apply flex items-center gap-6;
	}

	.action-link {
		@apply flex items-center gap-1.5 text-sm font-medium cursor-pointer border-none bg-transparent;
		color: #007acc;
	}
	.action-link:hover {
		text-decoration: underline;
	}

	.preset-select {
		@apply w-44;
	}
</style>
